/** @file applyPlants: fixed step/hook plants pull their IK chains to the planted world points after evaluate. */
import type { ClipEvent } from '@zilo/pose3d-format/types/clip';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Quat } from '@zilo/pose3d-math/quat/quat';
import { quatFromYawPitchRoll } from '@zilo/pose3d-math/quat/quatFromYawPitchRoll';
import { quatMul } from '@zilo/pose3d-math/quat/quatMul';
import { quatRotateVec3 } from '@zilo/pose3d-math/quat/quatRotateVec3';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { vec3Add } from '@zilo/pose3d-math/vec3/vec3Add';
import { vec3Sub } from '@zilo/pose3d-math/vec3/vec3Sub';
import { ikConjugate } from '@zilo/pose3d-pose/_internal/ikConjugate';
import { ikFromTo } from '@zilo/pose3d-pose/_internal/ikFromTo';
import { ikNormalize } from '@zilo/pose3d-pose/_internal/ikNormalize';
import type { PoseBuffer, PoseParams } from '@zilo/pose3d-pose/contract';
import { chainPose } from '@zilo/pose3d-pose/chain/chainPose';
import { rotateZScaled } from '@zilo/pose3d-pose/chain/rotateZScaled';
import { tiltOf } from '@zilo/pose3d-pose/chain/tiltOf';
import type { Plants } from '@zilo/pose3d-pose/events/eventTypes';
import type { IkResult } from '@zilo/pose3d-pose/ik/ikTypes';
import { solveCcd } from '@zilo/pose3d-pose/ik/solveCcd';
import { solveTwoBone } from '@zilo/pose3d-pose/ik/solveTwoBone';
import { prepareRig } from '@zilo/pose3d-pose/rig/prepareRig';
import type { IkChain, Rig } from '@zilo/pose3d-pose/rig/rigTypes';

const PLANTING: ReadonlySet<ClipEvent['type']> = new Set(['step', 'hook1', 'hook2', 'hook3']);
const PASSES = 4;
const CLOSE = 1e-4;

const posOf = (rig: Rig, i: number, zs: number): Vec3 => [rig.workPos[i * 3] ?? 0, rig.workPos[i * 3 + 1] ?? 0, (rig.workPos[i * 3 + 2] ?? 0) / zs];
const rotOf = (rig: Rig, i: number): Quat => [rig.workRot[i * 4] ?? 0, rig.workRot[i * 4 + 1] ?? 0, rig.workRot[i * 4 + 2] ?? 0, rig.workRot[i * 4 + 3] ?? 1];

/** `rz(rot, node.pos * scale)` of a node in z-divided rig space: the part of its bone its rotation turns. */
function ownPart(rig: Rig, i: number, scale: number, zs: number): Vec3 {
  return quatRotateVec3(rotOf(rig, i), [(rig.pos[i * 3] ?? 0) * scale, (rig.pos[i * 3 + 1] ?? 0) * scale, ((rig.pos[i * 3 + 2] ?? 0) * scale) / zs]);
}

/**
 * A world point relative to the entity mapped back into the rig frame: the inverse facing, then the inverse tilt about
 * the planted node's root pivot (21.9, as `chainPose` applies it), then un-mirrored.
 */
function rigTarget(rig: Rig, planted: number, offset: Vec3, params: PoseParams, zs: number): Vec3 {
  const b = rotateZScaled(ikConjugate(quatFromYawPitchRoll(params.bodyFacingDeg, 0, 0)), offset, zs);
  const tilt = tiltOf(params);
  const mirror = params.mirrorX === true;
  const pivots = params.tiltPivot === 'node' ? rig.workPos : rig.workOrigin;
  const root = (rig.workRoot[planted] ?? planted) * 3;
  const c: Vec3 = [(mirror ? -1 : 1) * (pivots[root] ?? 0), pivots[root + 1] ?? 0, pivots[root + 2] ?? 0];
  const r = tilt === null ? b : vec3Add(c, rotateZScaled(ikConjugate(tilt), vec3Sub(b, c), zs));
  return [mirror ? -r[0] : r[0], r[1], r[2]];
}

/** The chain of the nearest ancestor-or-self with one (decision 15), or null. */
function chainFor(rig: Rig, node: number): IkChain | null {
  for (let k = node; k >= 0; k = rig.parent[k] ?? -1) {
    const chain = rig.chains.get(k);
    if (chain !== undefined) return chain;
  }
  return null;
}

/** One solve pass: points in z-divided rig space, solved, written back as local rotations top-first. */
function solvePass(rig: Rig, chain: IkChain, planted: number, target: Vec3, params: PoseParams, out: PoseBuffer): void {
  const zs = params.profile.zScaleRotate ?? 1;
  const tail = vec3Sub(posOf(rig, planted, zs), posOf(rig, chain.end, zs));
  const points = [chain.pivot, ...chain.nodes].map((i) => posOf(rig, i, zs));
  const goal = vec3Sub([target[0], target[1], target[2] / zs], tail);
  const res: IkResult = { points: [], reached: false };
  const solved = chain.nodes.length === 2 ? solveTwoBone({ points, target: goal }, res) : solveCcd({ points, target: goal }, res);
  const locals = rig.workLocalsA;
  chain.nodes.forEach((node, k) => {
    const parent = rig.workParent[node] ?? -1;
    const local = locals[node];
    const from = solved.points[k];
    const to = solved.points[k + 1];
    if (parent < 0 || local === undefined || from === undefined || to === undefined) return;
    // Only the node's own `pos` turns with its rotation (18.3); offsets and key positions sit at the bone origin.
    const own = ownPart(rig, node, local.scale, zs);
    const origin = vec3Sub(posOf(rig, node, zs), own);
    const rot = quatMul(ikFromTo(ikNormalize(own), ikNormalize(vec3Sub(to, origin))), rotOf(rig, node));
    const q = quatMul(ikConjugate(rotOf(rig, parent)), rot);
    local.rot[0] = q[0]; local.rot[1] = q[1]; local.rot[2] = q[2]; local.rot[3] = q[3];
    chainPose(rig, locals, params, out);
  });
}

/**
 * Re-runs the chain on the locals of the last `evaluate` of `puppet` (`rig.workLocalsA`) into `out`, then, unless
 * `ikMode` is `off` (or `auto` with `pixelsPerUnit <= 32`, 15.12), pulls each `fixed` step/hook plant's chain so the
 * planted node reaches `point - entityPos` mapped into the rig frame (inverse facing and tilt, un-mirrored); a node
 * below the chain end is a rigid tail. Two-bone for 2 nodes, CCD otherwise, up to 4 passes to 1e-4 (decision 15).
 */
export function applyPlants(puppet: Puppet, plants: Plants, entityPos: Vec3, params: PoseParams, out: PoseBuffer): PoseBuffer {
  const rig = prepareRig(puppet);
  chainPose(rig, rig.workLocalsA, params, out);
  const mode = params.ikMode ?? 'auto';
  if (mode === 'off' || (mode === 'auto' && params.profile.pixelsPerUnit <= 32)) return out;
  const zs = params.profile.zScaleRotate ?? 1;
  for (const [name, plant] of plants) {
    const planted = rig.index.get(name);
    const chain = planted === undefined ? null : chainFor(rig, planted);
    if (planted === undefined || chain === null || plant.mode !== 'fixed' || !PLANTING.has(plant.type)) continue;
    const target = rigTarget(rig, planted, vec3Sub(plant.point, entityPos), params, zs);
    for (let pass = 0; pass < PASSES; pass++) {
      solvePass(rig, chain, planted, target, params, out);
      const at = posOf(rig, planted, 1);
      if (Math.hypot(at[0] - target[0], at[1] - target[1], at[2] - target[2]) <= CLOSE) break;
    }
  }
  return out;
}
